import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import { connect } from 'react-redux'
import { OverlayTrigger, Popover } from 'react-bootstrap'
import { Button, Label, Checkbox, Dialogs, Selector } from '@leverege/ui-elements'
import { Types, getAttributeCreatorFor, Icons } from '@leverege/api-attributes'
import BaseAttributeEditor from './BaseAttributeEditor.jsx'
import { 
  TextInput, LabelGroup, Section, Tags, Icon, FontIconSelector, Flex, 
  ColorSelector, JsonEditorDialog, InsetSection, 
} from '~/views'

import style from './style.css'
import OwnedPermissionsWarning from './OwnedPermissionsWarning.jsx'
import { BlueprintActions } from '../../actions/index.js'

const RelationshipDesc = {
  oneToOne : 'This attribute will reference one TGT instance. That instance can be referenced by only one SRC.',
  manyToOne : 'This attribute will reference one TGT instance. That instance can be referenced by many SRCs.',
  oneToMany : 'This attribute will reference many TGT instances. These instances can be referenced by only one SRCs.',
  manyToMany : 'This attribute will reference many TGT instances. These instances can be referenced by many SRCs.'
}

class RelationshipAttributeEditor extends React.Component {

  static propTypes = {
    attribute : PropTypes.object,
    blueprint : PropTypes.object,
    currentTags : PropTypes.array,
  }

  constructor( props ) {
    super( props )
    this.state = {
      showJsonEditor : false,
    }
    
    this.changePath = BaseAttributeEditor.ChangePath
  }

  onFontSelected = ( font ) => {
    this.changePath( 'icon/font', font )
  }

  onColorSelected = ( color ) => {
    this.changePath( 'icon/fontColor', color.rgb )
  }

  onEditMetadata = ( metadata ) => { 
    this.changePath( 'metadata', metadata, false )
    this.setState( { showJsonEditor : false } ) 
  }

  onChangeOwned = ( path, value, err ) => {
    const { attribute, blueprint } = this.props

    if ( !value ) {
      this.changePath( path, value, err )
      return
    }

    Dialogs.show( {
      component : OwnedPermissionsWarning,
      props : {
        attribute,
        blueprint,
        onOkay : () => { this.changePath( path, value, err ) }
      }
    } )
  }

  getDesc = ( v, tgt ) => {
    const { blueprint, attribute } = this.props
    
    let str = RelationshipDesc[v]
    str = str.replace( /SRC/g, blueprint.name )
    str = str.replace( /TGT/g, tgt?.name || 'target blueprint' )
    str = str.replace( /ATTR/g, attribute.name )
    return str
  }

  componentDidMount() {
    const { dispatch, blueprint } = this.props
    dispatch( BlueprintActions.listBlueprints( blueprint.projectId ) )
  }

  render() {
    const { showJsonEditor } = this.state 
    const { attribute : attr, typeName, currentTags, blueprints = {} } = this.props
    console.log( 'blueprints=', blueprints )
    if ( attr == null ) {
      return <div />
    }

    const attrCreator = getAttributeCreatorFor( attr.type )
    const attrIcon = Icons.getIcon( attr )
 
    const popover = (
      <Popover className={style.popover} style={{ minWidth : '300px' }} id="icon" title="Set Icon URL">
        <LabelGroup>
          <label>URL</label>
          <TextInput
            hint="Icon URL..."
            value={attr.icon?.url} 
            changeOnReturn 
            onChange={( v, err ) => { this.changePath( 'icon/url', v, err ) }} />
        </LabelGroup> 
        <div style={{ textAlign : 'center' }}>OR</div>
        <div className={style.fontOptions}>
          <LabelGroup>
            <label>Font</label>
            <FontIconSelector style={{ marginRight : '10px' }} onSelect={this.onFontSelected} font={attrIcon && attrIcon.fontClass} />
            <ColorSelector onChangeComplete={this.onColorSelected} />
          </LabelGroup>
        </div>
      </Popover>
    )

    const icon = (
      <OverlayTrigger key="icon" rootClose trigger="click" placement="left" overlay={popover}>
        <div className={classnames( style.iconHolder, attrIcon && style.hasIcon )} style={{ width : '40px', height : '40px' }}>
          {attrIcon == null ? <div className={style.noIcon}>SET ICON</div> : <Icon className={style.icon} icon={attrIcon} />}
        </div>
      </OverlayTrigger> 
    )

    let stringifiedMetadata
    if ( attr.metadata ) {
      stringifiedMetadata = JSON.stringify( attr.metadata, null, 2 )
    }
    const tBpName = blueprints?.index?.[attr.targetBlueprintId]?.name ? 
      `${blueprints.index[attr.targetBlueprintId].name} - ${attr.targetBlueprintId}` :
      attr.targetBlueprintId
    return (
      <Section title={typeName || attrCreator?.name()} subtitle={attr.path} titleOnTop right={icon}>
        <Flex variant="vSpace" align="stretch" direction="column">
          <LabelGroup>
            <label>Name</label>
            <TextInput
              value={attr.name} 
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'name', v, err ) }} />
          </LabelGroup>
          <LabelGroup>
            <label>Description</label>
            <TextInput
              value={attr.desc}
              changeOnReturn
              size={10}
              onChange={( v, err ) => { this.changePath( 'desc', v, err ) }} />
          </LabelGroup>

          <LabelGroup>
            <label>Tags</label>
            <Tags
              tags={attr.tags}
              autofocus={false}
              suggestions={currentTags}
              tagAdded={( tag, arr ) => { this.changePath( 'tags', arr, false ) }}
              tagRemoved={( tag, arr ) => { this.changePath( 'tags', arr, false ) }} />
          </LabelGroup>

          <Flex variant="noSpace">
            <LabelGroup style={{ flexBasis : '200px', marginRight : '8px' }}>
              <Label>Source</Label>
              <Selector
                values={Types.Source.values()}
                value={attr.source}
                onChange={( value ) => { this.changePath( 'source', value.value, false ); }} />
            </LabelGroup>

            <Flex direction="row">
              <Checkbox
                value={attr.controllable} 
                onChange={( evt ) => { this.changePath( 'controllable', evt.value, false ) }} />
              Controllable
            </Flex>

          </Flex>

          <Flex direction="row">
            <Checkbox
              value={attr.owned} 
              onChange={( evt ) => { this.onChangeOwned( 'owned', evt.value, false ) }} />
            Delete on Remove from Parent?
          </Flex>

          <LabelGroup>
            <label>Metadata</label>
            <Flex>
              <Button 
                variant="secondarySmall"
                className={style.addMetadataButton}
                onClick={() => this.setState( { showJsonEditor : true } )}>
                {stringifiedMetadata ? 'Edit' : 'Add'}
              </Button>
            </Flex>
          </LabelGroup>
          
          <InsetSection className={style.inset}>
            <div className={stringifiedMetadata ? style.metadata : style.noMetadata}>
              {stringifiedMetadata || 'No Metadata' }
            </div> 
          </InsetSection>
          <Flex>
            <JsonEditorDialog 
              title="Parent Relationship Attribute Metadata"
              message="Enter the JSON object for the parent relationship attribute metadata"
              show={showJsonEditor}
              onOk={this.onEditMetadata}
              onCancel={() => this.setState( { showJsonEditor : false } )}
              value={attr.metadata} />
          </Flex>
          
          <LabelGroup>
            <label>Blueprint</label>
            <TextInput disabled value={tBpName} />
          </LabelGroup>

          <LabelGroup >
            <label>Relationship</label>
            <TextInput disabled value={attr.relationshipType} />
          </LabelGroup>

          <LabelGroup >
            <label>Child Path</label>
            <TextInput disabled value={attr.childPath} />
          </LabelGroup>

          {attr.relationshipType === 'oneToOne' && (
            <LabelGroup>
              <label>Forward Data</label>
              <Checkbox
                className={style.forwardData}
                value={attr.forwardData}
                onChange={evt => this.changePath( 'forwardData', evt.value )} />
            </LabelGroup>
          )}

          <div style={{ marginLeft : '8px', marginRight : '8px', whiteSpace : 'normal' }}>
            {this.getDesc( attr.relationshipType )}
          </div>

        </Flex>
        
      </Section>
    ) 
  }

}

export default connect( ( state ) => { 
  return { 
    blueprints : state.blueprints,
  }
} )( RelationshipAttributeEditor )
